import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Steps,
  Tag,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  BulbOutlined,
  RocketOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import {
  getProducts,
  getProductionOnboardingSuggestions,
  getProductionOnboardingTemplates,
} from "./api";
import { getProductionTerminology } from "./productionTerminology";

const { Title, Text } = Typography;
const DEFAULT_TERMINOLOGY = getProductionTerminology([]);

const normalizeCount = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : fallback;
};

const extractCollectionCount = (payload, key) => {
  if (!payload || typeof payload !== "object") {
    return 0;
  }

  if (Number.isFinite(Number(payload.total))) {
    return normalizeCount(payload.total);
  }

  if (Array.isArray(payload[key])) {
    return payload[key].length;
  }

  return 0;
};

const ProductionSetupAssistantModal = ({
  visible,
  warehouseId,
  companyId,
  productionUnitCount = 0,
  canCreateRawMaterials = false,
  canCreateProductionUnits = false,
  terminology = DEFAULT_TERMINOLOGY,
  onCancel,
  onOpenOnboarding,
  onOpenRawMaterials,
  onOpenUnitCreation,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPath, setSelectedPath] = useState("suggestions");
  const [loading, setLoading] = useState(false);
  const [diagnostic, setDiagnostic] = useState({
    rawMaterialCount: 0,
    semiFinishedCount: 0,
    suggestionCount: 0,
    templateCount: 0,
    error: null,
  });

  const recommendedPath = useMemo(() => {
    if (diagnostic.rawMaterialCount === 0 && canCreateRawMaterials) {
      return "raw-materials";
    }

    if (
      productionUnitCount === 0 &&
      (diagnostic.suggestionCount > 0 || diagnostic.templateCount > 0) &&
      canCreateProductionUnits
    ) {
      return "suggestions";
    }

    if (canCreateProductionUnits) {
      return "unit";
    }

    return "suggestions";
  }, [
    canCreateProductionUnits,
    canCreateRawMaterials,
    diagnostic.rawMaterialCount,
    diagnostic.suggestionCount,
    diagnostic.templateCount,
    productionUnitCount,
  ]);

  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      setSelectedPath("suggestions");
      setLoading(false);
      setDiagnostic({
        rawMaterialCount: 0,
        semiFinishedCount: 0,
        suggestionCount: 0,
        templateCount: 0,
        error: null,
      });
      return;
    }

    if (!warehouseId || !companyId) {
      setDiagnostic((current) => ({
        ...current,
        error:
          "Selectionnez d'abord une entreprise et un établissement avant de lancer l'assistant.",
      }));
      return;
    }

    let active = true;

    const loadDiagnostic = async () => {
      setLoading(true);

      const [rawProductsResult, semiFinishedResult, suggestionsResult, templatesResult] =
        await Promise.allSettled([
          getProducts("", warehouseId, "raw", { page: 1, limit: 1 }),
          getProducts("", warehouseId, "production", {
            page: 1,
            limit: 1,
            production_kind: "semi_finished",
          }),
          getProductionOnboardingSuggestions({ company_id: companyId }),
          getProductionOnboardingTemplates({ company_id: companyId }),
        ]);

      if (!active) {
        return;
      }

      setDiagnostic({
        rawMaterialCount:
          rawProductsResult.status === "fulfilled"
            ? extractCollectionCount(rawProductsResult.value, "products")
            : 0,
        semiFinishedCount:
          semiFinishedResult.status === "fulfilled"
            ? extractCollectionCount(semiFinishedResult.value, "products")
            : 0,
        suggestionCount:
          suggestionsResult.status === "fulfilled"
            ? extractCollectionCount(suggestionsResult.value, "suggestions")
            : 0,
        templateCount:
          templatesResult.status === "fulfilled"
            ? extractCollectionCount(templatesResult.value, "templates")
            : 0,
        error:
          rawProductsResult.status === "rejected" &&
          semiFinishedResult.status === "rejected" &&
          suggestionsResult.status === "rejected" &&
          templatesResult.status === "rejected"
            ? "Le diagnostic n'a pas pu etre charge. Vous pouvez quand meme choisir une action manuellement."
            : null,
      });

      setLoading(false);
    };

    loadDiagnostic().catch(() => {
      if (!active) {
        return;
      }

      setDiagnostic((current) => ({
        ...current,
        error:
          "Le diagnostic n'a pas pu etre charge. Vous pouvez quand meme choisir une action manuellement.",
      }));
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyId, visible, warehouseId]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSelectedPath((current) => {
      if (current && current !== "suggestions") {
        return current;
      }

      return recommendedPath;
    });
  }, [recommendedPath, visible]);

  const routeOptions = [
    {
      key: "raw-materials",
      title: `Créer mes ${terminology.materialPlural}`,
      description: `Passez par la création de vos ${terminology.materialPlural} avant la ${terminology.configurationSingular} pour cadrer les unités de stock, les conditionnements d'achat, les seuils et les prix unitaires.`,
      icon: <ShoppingOutlined style={{ fontSize: 18, color: "#ad6800" }} />,
      helper:
        diagnostic.rawMaterialCount > 0
          ? `${diagnostic.rawMaterialCount} ${terminology.materialPlural} déjà disponibles`
          : `${terminology.noMaterialDetected} dans cet établissement`,
      disabled: !canCreateRawMaterials,
      disabledReason:
        "L'acces a la creation des produits est requis pour ouvrir cette etape.",
    },
    {
      key: "suggestions",
      title: "Initialiser à partir de suggestions",
      description: `Utilisez les ${terminology.configurationPlural} suggérées ou un pack rapide pour précharger des ${terminology.materialPlural}, des ${terminology.unitPlural} et des ${terminology.configurationPlural} types.`,
      icon: <BulbOutlined style={{ fontSize: 18, color: "#1677ff" }} />,
      helper: `${diagnostic.suggestionCount} suggestion(s) et ${diagnostic.templateCount} pack(s) disponibles`,
      disabled: !canCreateProductionUnits,
      disabledReason:
        `La permission de création des ${terminology.unitPlural} est requise pour appliquer un pack.`,
    },
    {
      key: "unit",
      title: `Créer directement une ${terminology.unitSingular}`,
      description: `Ouvrez le formulaire de ${terminology.configurationSingular} lorsque votre base de ${terminology.materialPlural} est déjà assez stable pour saisir les quantités.`,
      icon: <RocketOutlined style={{ fontSize: 18, color: "#389e0d" }} />,
      helper:
        productionUnitCount > 0
          ? `${productionUnitCount} ${terminology.unitPlural} déjà configurées`
          : `Aucune ${terminology.unitSingular} détectée dans cet établissement`,
      disabled: !canCreateProductionUnits,
      disabledReason:
        `La permission de création des ${terminology.unitPlural} est requise pour ouvrir le formulaire.`,
    },
  ];

  const selectedRoute = routeOptions.find((item) => item.key === selectedPath);

  const diagnosticAlert = useMemo(() => {
    if (!warehouseId || !companyId) {
      return {
        type: "warning",
        message: "Contexte incomplet",
        description: `Sélectionnez d'abord l'établissement cible. L'assistant en a besoin pour diagnostiquer vos ${terminology.materialPlural} et vos ${terminology.configurationPlural}.`,
      };
    }

    if (diagnostic.rawMaterialCount === 0 && canCreateRawMaterials) {
      return {
        type: "warning",
        message: `Point de départ recommandé : les ${terminology.materialPlural}`,
        description: `${terminology.noUsableMaterialSentence} Commencez ici pour stabiliser vos unités de stock avant de définir les ${terminology.configurationPlural}.`,
      };
    }

    if (
      productionUnitCount === 0 &&
      (diagnostic.suggestionCount > 0 || diagnostic.templateCount > 0)
    ) {
      return {
        type: "info",
        message: "Acceleration possible avec les suggestions",
        description: `Vous pouvez précharger une base de travail depuis les suggestions et les packs, puis ajuster les ${terminology.configurationPlural} selon votre réalité terrain.`,
      };
    }

    return {
      type: "success",
      message: "Base deja amorcee",
      description: `Votre établissement contient déjà des ${terminology.materialPlural} ou des ${terminology.unitPlural}. L'assistant peut maintenant servir surtout de raccourci vers la bonne étape.`,
    };
  }, [
    canCreateRawMaterials,
    companyId,
    diagnostic.rawMaterialCount,
    diagnostic.suggestionCount,
    diagnostic.templateCount,
    productionUnitCount,
    terminology,
    warehouseId,
  ]);

  const handleLaunch = () => {
    if (!selectedRoute || selectedRoute.disabled) {
      return;
    }

    if (selectedRoute.key === "raw-materials") {
      onOpenRawMaterials?.();
      return;
    }

    if (selectedRoute.key === "unit") {
      onOpenUnitCreation?.();
      return;
    }

    onOpenOnboarding?.("suggestions");
  };

  return (
    <Modal
      title="Assistant de configuration Production"
      open={visible}
      onCancel={onCancel}
      width={980}
      destroyOnClose
      okButtonProps={{ style: { display: "none" } }}
      cancelButtonProps={{ style: { display: "none" } }}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            {currentStep > 0 && (
              <Button
                type="link"
                onClick={() => setCurrentStep((step) => Math.max(step - 1, 0))}
              >
                Retour
              </Button>
            )}
          </Space>
          <Space>
            <Button type="link" onClick={onCancel}>
              Fermer
            </Button>
            {currentStep === 0 ? (
              <Button
                type="primary"
                onClick={() => setCurrentStep(1)}
                disabled={loading || !warehouseId || !companyId}
              >
                Continuer
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleLaunch}
                disabled={!selectedRoute || selectedRoute.disabled}
              >
                {selectedRoute?.key === "raw-materials"
                  ? `Ouvrir les ${terminology.materialPlural}`
                  : selectedRoute?.key === "unit"
                    ? `Ouvrir le formulaire de ${terminology.configurationSingular}`
                    : "Ouvrir les suggestions"}
              </Button>
            )}
          </Space>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Text type="secondary">
          Cet assistant vous aide a choisir le meilleur point d'entree pour
          configurer la production sans changer votre logique metier actuelle.
        </Text>

        <Steps
          current={currentStep}
          items={[
            { title: "Diagnostic" },
            { title: "Choix du parcours" },
          ]}
        />

        {currentStep === 0 ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Alert
              showIcon
              type={diagnosticAlert.type}
              message={diagnosticAlert.message}
              description={diagnosticAlert.description}
            />

            {diagnostic.error && (
              <Alert showIcon type="error" message={diagnostic.error} />
            )}

            {loading ? (
              <div style={{ padding: "32px 0", textAlign: "center" }}>
                <Spin />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title={terminology.materialPluralTitle}
                      value={normalizeCount(diagnostic.rawMaterialCount)}
                      prefix={<ShoppingOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title="Semi-finis"
                      value={normalizeCount(diagnostic.semiFinishedCount)}
                      prefix={<AppstoreOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card>
                    <Statistic
                      title={terminology.unitsTitle}
                      value={normalizeCount(productionUnitCount)}
                      prefix={<RocketOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            <Card size="small">
              <Space direction="vertical" size={4}>
                <Title level={5} style={{ margin: 0 }}>
                  Ce que l'assistant prend en compte
                </Title>
                <Text type="secondary">
                  Les {terminology.configurationPlural} suggérées et les packs réutilisent les briques
                  déjà présentes dans le module : catégories, {terminology.unitPlural}, {terminology.materialPlural} et{" "}
                  {terminology.configurationPlural} types.
                </Text>
                <Text type="secondary">
                  La saisie manuelle reste disponible. L'assistant sert surtout a
                  reduire la charge de configuration initiale.
                </Text>
              </Space>
            </Card>
          </Space>
        ) : (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Alert
              showIcon
              type="info"
              message="Choisissez votre prochain point d'entree"
              description={`Vous pourrez toujours revenir à l'assistant depuis la liste des ${terminology.unitPlural}.`}
            />

            <Row gutter={[16, 16]}>
              {routeOptions.map((option) => {
                const isSelected = option.key === selectedPath;
                const isRecommended = option.key === recommendedPath;

                return (
                  <Col xs={24} key={option.key}>
                    <Card
                      hoverable={!option.disabled}
                      onClick={() => {
                        if (!option.disabled) {
                          setSelectedPath(option.key);
                        }
                      }}
                      style={{
                        borderColor: isSelected ? "#1677ff" : undefined,
                        boxShadow: isSelected
                          ? "0 0 0 2px rgba(22, 119, 255, 0.12)"
                          : undefined,
                        opacity: option.disabled ? 0.65 : 1,
                      }}
                    >
                      <Space
                        direction="vertical"
                        size={8}
                        style={{ width: "100%" }}
                      >
                        <Space wrap>
                          {option.icon}
                          <Text strong>{option.title}</Text>
                          {isRecommended && <Tag color="blue">Recommande</Tag>}
                          {isSelected && <Tag color="processing">Selectionne</Tag>}
                        </Space>
                        <Text type="secondary">{option.description}</Text>
                        <Text>{option.helper}</Text>
                        {option.disabled && (
                          <Text type="danger">{option.disabledReason}</Text>
                        )}
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Space>
        )}
      </Space>
    </Modal>
  );
};

export default ProductionSetupAssistantModal;
